import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { InventoryProduct, InventoryLogWithActor } from '@/lib/integrations/supabase/types';

export async function getInventoryProducts(businessId: string): Promise<InventoryProduct[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('inventory_products')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function getInventoryProduct(id: string): Promise<InventoryProduct | null> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('inventory_products')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

export async function getLowStockProducts(businessId: string): Promise<InventoryProduct[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase.rpc('get_low_stock_products', {
    p_business_id: businessId,
  } as any);

  if (error) {
    throw error;
  }

  return (data || []) as InventoryProduct[];
}

export async function getInventoryProductsByCategory(
  businessId: string,
  category: string
): Promise<InventoryProduct[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('inventory_products')
    .select('*')
    .eq('business_id', businessId)
    .eq('category', category)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function getInventoryLogs(
  productId: string,
  limit: number = 50
): Promise<InventoryLogWithActor[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('inventory_logs')
    .select(`
      *,
      actor:profiles!inventory_logs_actor_id_fkey(*)
    `)
    .eq('product_id', productId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  return (data || []) as InventoryLogWithActor[];
}

export async function getBusinessInventoryLogs(
  businessId: string,
  limit: number = 100
): Promise<InventoryLogWithActor[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('inventory_logs')
    .select(`
      *,
      actor:profiles!inventory_logs_actor_id_fkey(*)
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  return (data || []) as InventoryLogWithActor[];
}

export function getStockStatus(product: InventoryProduct): 'in_stock' | 'low_stock' | 'out_of_stock' {
  if (product.quantity === 0) return 'out_of_stock';
  if (product.quantity <= product.min_threshold) return 'low_stock';
  return 'in_stock';
}

export function getStockStatusBadge(status: 'in_stock' | 'low_stock' | 'out_of_stock'): string {
  switch (status) {
    case 'in_stock':
      return 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
    case 'low_stock':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400';
    case 'out_of_stock':
      return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400';
  }
}

export function getStockStatusLabel(status: 'in_stock' | 'low_stock' | 'out_of_stock'): string {
  switch (status) {
    case 'in_stock':
      return 'In Stock';
    case 'low_stock':
      return 'Low Stock';
    case 'out_of_stock':
      return 'Out of Stock';
  }
}

export function getChangeTypeLabel(changeType: 'manual_adjustment' | 'usage' | 'restock'): string {
  switch (changeType) {
    case 'manual_adjustment':
      return 'Manual Adjustment';
    case 'usage':
      return 'Usage';
    case 'restock':
      return 'Restock';
  }
}
